<!-- This layout page is used to setup various global variables for the app -->
<script setup>
const { initialize } = useHotjar();
const client = useSupabaseClient();

// 
// Global States/Cookies:
// 

// User State
const auth = useSupabaseUser();
const user = useState('user');
const useMunicipalityBranding = useCookie('useMunicipalityBranding');
user.value = await getUserFromAuth();
useMunicipalityBranding.value = user.value?.use_municipality_branding;

// Municipalities State
const municipalities = await useCachedData('public', 'municipalities');
useState('municipalities', () => municipalities);

// Municipality State
const municipality = useCookie('municipality');

// Response Message State
const responseMessage = useState('responseMessage');
responseMessage.value = {
  message: '',
  success: false,
  key: 0,
};

// Feedback Modal State
const openFeedbackModal = useState('openFeedbackModal');

// 
// Misc Functions:
// 

// Load user data from profiles table using the auth user id FK
// Only actually fetches from the DB if the user logs in for the first time and their profile data is not set
async function getUserFromAuth() {
  if (auth.value) {
    const profileDataCookie = useCookie('profileData', { maxAge: 60 * 60 * 24 * 30 });
    if (!profileDataCookie.value) {
      const { data, error } = await client.from('profiles').select().eq('id', auth.value.id).single();
      if (!error) {
        profileDataCookie.value = data;
      }
      else {
        console.error('Error loading user data:', error.message);
        return null;
      }
    }

    return profileDataCookie.value;
  }
}

// Function that gets the municipality from the subdomain or the user's municipality column
// Only actually fetches from the DB if the user logs in for the first time and their municipality is not set
async function getMunicipality() {
  const domainToStateNameConversions = {
    'mississippimills': 'mm',
    'crowsnestpass': 'crowsnest',
  };

  const headers = useRequestHeaders(['host']);
  const host = headers.host || '';
  let domainPartsArr = host.split('.');
  let subDomain = domainPartsArr.length > 1 ? domainPartsArr[0] : null;
  // Convert subdomain to state name if necessary (e.g. 'mississippimills' -> 'mm')
  subDomain = domainToStateNameConversions[subDomain] || subDomain;

  const municipalityExists = subDomain ? municipalities.some(m => m.name === subDomain) : false;

  if (municipalityExists) {
    municipality.value = subDomain;
  }
  else {
    // Default to the user's municipality if no municipalitySubDomain is present or
    if (user.value?.municipality && user.value.municipality != 'none') {
      municipality.value = user.value.municipality;
    }
    else if (!user.value) {
      // Default to nothing if no user logged in or municipality is 'none'
      municipality.value = null;
    }
  }
}

// Hanging Request Bug Fix
// Function to refresh the token periodically before it expires
async function refreshToken() {
  try {
    const { currentRoute } = useRouter();
    if (currentRoute.value.path.includes('editor')) {
      // Don't refresh token if user is on an editor page
      console.log('Attempted to refresh token while on editor page, skipping');
      return;
    }
    else if (!auth.value) {
      await refreshNuxtData();
      console.log('Data refreshed (no auth)');
    }
    else {
      const { data, error } = await client.auth.refreshSession();
      if (error) throw error;

      // Update cookies with new access token
      if (data.session) {
        const accessTokenCookie = useCookie('sb-access-token');
        accessTokenCookie.value = data.session.access_token;
        console.log('Access token refreshed and cookie updated');
      }
    }

    // Also refresh the page when the user's token is refreshed - another attempt to fix the hanging requests bug
    document.location.reload();
  } catch (error) {
    console.error('Error refreshing auth token:', error.message);
  }
}

// Setup periodic token refresh
let refreshInterval;
function setupTokenRefresh() {
  // 1 hour interval in milliseconds
  const intervalSeconds = 60 * 60 * 1000;
  refreshInterval = setInterval(refreshToken, intervalSeconds);
}

// Reset token interval when user logs in/out
watch(auth, (newVal, oldVal) => {
  if (!newVal) {
    // User logged out, clear the interval
    clearInterval(refreshInterval);
  }
});

const primaryColorHex = ref('');

onUnmounted(() => {
  clearInterval(refreshInterval);
});

// Stuff that goes in onMounted is stuff that has to be done on the client side and is ok to do after the page has loaded
onMounted(async () => {
  setupTokenRefresh();

  // Initialize Hotjar
  initialize();

  await getMunicipality();

  // Get the primaryColorHex for the nuxtLoadingIndicator
  primaryColorHex.value = getTailwindColorHex('--color-primary-500');
});

// We want to hide the navbar on some pages
const currentRoute = useRoute();
const routeFoldersWithoutNavBar = ['/auth'];
const showNavBar = computed(() => {
  return !routeFoldersWithoutNavBar.some(folder => currentRoute.path.includes(folder));
});
</script>

<template>
  <UApp>
    <div class="font-sans flex flex-col min-h-screen justify-between" :class="municipality">
      <NuxtLoadingIndicator :color="primaryColorHex" />
      <NavBar v-if="showNavBar" />
      <ResponseMessage />
      <slot />
      <Footer />

      <!-- Portal container for popovers: fix for bug with popovers appearing above modal -->
      <div id="popover-container" class="z-30"></div>
    </div>

    <ClientOnly>
      <LazyModal ref="openFeedbackModal" title="Help Us Improve!">
        <FeedbackForm />
      </LazyModal>
    </ClientOnly>
  </UApp>
</template>
